import React, { useMemo } from 'react';
import AsyncSelect from 'react-select/async';
import { components as defaultComponents, ControlProps, CSSObjectWithLabel } from 'react-select';
import { Icon, colors } from 'common-components';
import { debounce } from '@helpers/debounce';
import { useAppTranslation } from '@hooks';

const searchOptionStyle = (isFocused: boolean): React.CSSProperties => ({
  margin: '2px 6px',
  padding: '10px 10px 10px 8px',
  width: 'auto',
  borderRadius: '6px',
  background: isFocused ? colors.gray50 : 'transparent',
  color: colors.gray900,
  fontSize: '16px',
  lineHeight: '24px',
  fontWeight: 500,
  cursor: 'pointer',
});

export const searchSelectStyles = {
  control: (provided: CSSObjectWithLabel) => ({
    ...provided,
    display: 'flex',
    height: '44px',
    padding: '0 14px',
    alignItems: 'center',
    gap: '8px',
    alignSelf: 'stretch',
    borderRadius: '8px',
    border: '1px solid #D0D5DD',
    background: '#FFF',
    boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    fontSize: '16px',
    lineHeight: '24px',
    ':hover': {
      borderColor: '#D0D5DD',
    },
  }),
  valueContainer: (provided: CSSObjectWithLabel) => ({
    ...provided,
    padding: 0,
  }),
  placeholder: (provided: CSSObjectWithLabel) => ({
    ...provided,
    margin: 0,
    color: colors.gray500,
  }),
  singleValue: (provided: CSSObjectWithLabel) => ({
    ...provided,
    margin: 0,
    color: colors.gray900,
  }),
  menu: (provided: CSSObjectWithLabel) => ({
    ...provided,
    marginTop: '4px',
    borderRadius: '8px',
    border: `1px solid ${colors.gray200}`,
    boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
  }),
  menuList: (provided: CSSObjectWithLabel) => ({
    ...provided,
    maxHeight: '320px',
    padding: '4px 0',
  }),
  option: (provided: CSSObjectWithLabel, { isFocused }: { isFocused: boolean }) => ({
    ...provided,
    ...searchOptionStyle(isFocused),
  }),
  dropdownIndicator: () => ({}),
  indicatorSeparator: () => ({}),
};

export const SearchControl: React.FC<ControlProps<any, boolean>> = ({ children, ...props }) => {
  return (
    <defaultComponents.Control {...props}>
      <Icon width={20} height={20} color={colors.gray500} icon="SearchLg" />
      {children}
    </defaultComponents.Control>
  );
};

export type ValueSelectType = {
  value: string
  label: string
};
type SelectPropsType<T extends ValueSelectType> = {
  loadOptions: (inputValue: string, callback: (options: T[]) => void) => void
  value: T | ''
  valuesSelected?: string[]
  onChange: (value: T | '') => void
  disabled?: boolean
};

const SelectAsync = <T extends ValueSelectType>({
  loadOptions,
  value,
  valuesSelected = [],
  onChange,
  disabled,
}: SelectPropsType<T>) => {
  const { translate } = useAppTranslation();
  const debouncedLoadOptions = useMemo(() => debounce(loadOptions, 500), [loadOptions]);

  return (
    <AsyncSelect
      cacheOptions
      defaultOptions
      loadOptions={debouncedLoadOptions}
      value={value}
      isClearable
      placeholder={translate('display.search')}
      isSearchable
      isDisabled={disabled}
      onChange={onChange}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      components={{
        Control: SearchControl,
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
        ClearIndicator: (props) => (
          <defaultComponents.ClearIndicator {...props}>
            <Icon width={18} height={18} icon="XClose" />
          </defaultComponents.ClearIndicator>
        ),
        Option: ({ innerProps, innerRef, children, isFocused }) => {
          const include = valuesSelected.includes(children?.toString());
          if (include) return null;
          return (
            <div
              style={searchOptionStyle(isFocused)}
              ref={innerRef}
              {...innerProps}
            >
              {children}
            </div>
          );
        },
      }}
      styles={{
        ...searchSelectStyles,
        menuPortal: (base) => ({
          ...base,
          zIndex: 9999,
        }),
      }}
    />
  );
};

export default SelectAsync;
